import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { Section } from '@wix/da-a11y/pkg/components/headings/tagWrappers';
import CorePickerStar from '@wix/da-ds/pkg/Icons/CorePickerStar';
import CorePickerTwinkle from '@wix/da-ds/pkg/Icons/CorePickerTwinkle';
import CorePickerStarCross from '@wix/da-ds/pkg/Icons/CorePickerStarCross';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { PapiSubscription } from '@wix/da-papi-types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import BackButton from '../../../BackButton';
import { type PackagePickerStoreSlice } from '../../../../redux/packagePicker/types';
import SaleCountdown from '../SaleCountdown';
import OriginalHeroHeader from './HeroHeader';
import SaleHeroHeader from './SaleHeroHeader';
import HeroTableDesktop from './HeroTableDesktop';
import HeroTableMobile from './HeroTableMobile';
import { getSaleEndDate } from '../../utils';

import s from './Hero.scss';

export interface Props {
  userJoinDate?: string;
  hasCore?: boolean;
  giftUser?: PackagePickerStoreSlice['giftUser'];
  referer: string;
  currentCoreSubscription?: PapiSubscription;
  sale?: PackagePickerStoreSlice['sale'];
}

const Hero: React.FC<Props> = ({
  userJoinDate,
  giftUser,
  referer,
  hasCore,
  currentCoreSubscription,
  sale,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);

  const refererIsSubscriptions = /subscriptions/.test(referer);
  const HeroHeader = sale ? SaleHeroHeader : OriginalHeroHeader;

  const { saleBackgroundUrl, ppShowCountdown, ppShowPreHeader } = sale ?? {};

  const saleEndDate = getSaleEndDate(sale, userJoinDate);

  return (
    <Section
      className={cn(s['root'], saleBackgroundUrl && s['sale-background'])}
      style={{
        backgroundImage: saleBackgroundUrl
          ? `url(${saleBackgroundUrl})`
          : undefined,
      }}
    >
      {refererIsSubscriptions && (
        <div className={s['back']}>
          <BackButton href={referer}>{t('common.back')}</BackButton>
        </div>
      )}
      <CorePickerStar
        className={cn(s['star'], s['star-1'])}
        size={IconSize.HUGE}
        disableDefaultColors
      />
      <CorePickerStar
        className={cn(s['star'], s['star-2'])}
        size={IconSize.HUGE}
        disableDefaultColors
      />
      <CorePickerStarCross
        className={cn(s['star'], s['star-3'])}
        size={IconSize.HUGE}
        disableDefaultColors
      />
      <CorePickerTwinkle
        className={cn(s['twinkle'], s['twinkle-1'])}
        disableDefaultColors
        size={IconSize.SMALL}
      />
      <CorePickerTwinkle
        className={cn(s['twinkle'], s['twinkle-2'])}
        disableDefaultColors
        size={IconSize.SMALL}
      />
      {ppShowCountdown && <SaleCountdown sale={sale} />}
      <HeroHeader
        hasCore={hasCore}
        showSubtitle={ppShowPreHeader}
        giftUser={giftUser}
        currentCoreSubscription={currentCoreSubscription}
        sale={sale}
      />

      {isMobile ? (
        <HeroTableMobile
          sale={sale}
          saleEndDate={saleEndDate}
          referer={referer}
        />
      ) : (
        <HeroTableDesktop
          sale={sale}
          saleEndDate={saleEndDate}
          referer={referer}
        />
      )}
    </Section>
  );
};
Hero.displayName = 'Hero';

export default Hero;
